import { hasPaidPropertyAccess } from '../../_helpers/subscription-access';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, throwError, map, switchMap, catchError, shareReplay, distinctUntilChanged, Subscription, defer, tap, BehaviorSubject, combineLatest } from 'rxjs';
import { PropertyService } from './property.service';
import { CiReport, mapCiReport } from '../../_components/search/view-area/ci-report';
import { AuthService } from '../auth.service';
import { canAccessPropertyFeature, canAccessProperty, propertyFeatureSession } from '../../_helpers/property-feature-access';

@Injectable({ providedIn: 'root' })
export class CiReportService implements OnDestroy {
  private reports = new Map<string | object, Observable<CiReport | null>>();
  private handles = new Map<string | object, { property$: BehaviorSubject<any>; report$: Observable<CiReport | null> }>();
  private dashboard$: Observable<any>;
  private readonly sessionSubscription: Subscription;

  constructor(private readonly _PS: PropertyService, private readonly auth: AuthService) {
    this.dashboard$ = this.dashboardRequest();
    this.sessionSubscription = auth.user$.pipe(
      map(propertyFeatureSession), distinctUntilChanged(),
    ).subscribe(() => {
      this.reports.clear();
      this.handles.clear();
      this.dashboard$ = this.dashboardRequest();
    });
  }

  getReport(property: any): Observable<CiReport | null> {
    if (!property) return of(null);
    const key = property._id || property;
    const existing = this.handles.get(key);
    if (existing) {
      // Selecting a property again must recheck a previous miss, and richer
      // property payloads must update both card and details subscriptions.
      existing.property$.next(property);
      return existing.report$;
    }
    const property$ = new BehaviorSubject(property);
    const report$ = combineLatest([
      this.auth.user$.pipe(distinctUntilChanged((a, b) => propertyFeatureSession(a) === propertyFeatureSession(b))),
      property$,
    ]).pipe(
      switchMap(([user, item]) => {
        if (canAccessPropertyFeature(user, 'intelligence')) return this.loadReport(item);
        if (!(user?.data ?? user) || hasPaidPropertyAccess(user)) return of(null);
        return this._PS.getViewPropertyStatus().pipe(
          switchMap(status => canAccessProperty(user, status, item._id) ? this.loadReport(item) : of(null)),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.handles.set(key, { property$, report$ });
    return report$;
  }

  /** Card previews expose only the listing summary, never report calculations. */
  getPreview(property: any): Observable<CiReport | null> {
    return this.auth.user$.pipe(
      distinctUntilChanged((a, b) => propertyFeatureSession(a) === propertyFeatureSession(b)),
      switchMap(user => {
        const embedded = mapCiReport(property);
        const source = canAccessPropertyFeature(user, 'intelligence')
          ? this.getReport(property) : of(embedded);
        return source.pipe(map(report => report ? {
          propertyType: report.propertyType, propertyName: report.propertyName,
          verdict: report.verdict, summary: report.summary, tags: report.tags,
          rentalRating: report.rentalRating, negotiationRating: report.negotiationRating,
          pricing: [], offers: [],
          valuation: report.valuation.filter(metric => metric.label === 'Rental Yield'),
        } : null));
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  ngOnDestroy(): void {
    this.sessionSubscription.unsubscribe();
    this.reports.clear();
    this.handles.clear();
  }

  private dashboardRequest(): Observable<any> {
    // Construct the existing cold request lazily, after property access is confirmed.
    return defer(() => this._PS.getCiDashboard()).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private loadReport(property: any): Observable<CiReport | null> {
    if (!property) return of(null);
    const key = property._id || property;
    const embedded = mapCiReport(property);
    if (embedded) {
      const report$ = of(embedded);
      this.reports.set(key, report$);
      return report$;
    }
    const cached = this.reports.get(key);
    if (cached) return cached;
    const report$ = (
      property._id
        ? this._PS.getPropertieByID(property._id).pipe(map(response => ({ ...property, ...response?.data })))
        : of(property)
    ).pipe(
      switchMap(item => this.resolveCiReport(item)),
      tap(report => {
        // Never let an older request replace a newer embedded report.
        if (this.reports.get(key) !== report$) return;
        if (report) this.reports.set(key, of(report));
        else this.reports.delete(key);
      }),
      catchError(error => {
        if (this.reports.get(key) === report$) this.reports.delete(key);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.reports.set(key, report$);
    return report$;
  }

  private resolveCiReport(property: any): Observable<CiReport | null> {
    const embedded = mapCiReport(property);
    if (embedded) return of(embedded);
    const reference = property.ciId ?? property.ciRecordId ?? property.ciCalculationId
      ?? property.intelligenceId ?? property.circledIntelligenceId;
    const id = typeof reference === 'string' ? reference : reference?._id;
    if (!id) return this.findGeneratedCi(property);
    return this._PS.getCiRecord(id).pipe(
      map(response => mapCiReport({ ...property, ciRecord: response?.data })),
      switchMap(report => report ? of(report) : this.findGeneratedCi(property)),
      catchError(error => error?.status === 404
        ? this.findGeneratedCi(property)
        : throwError(() => error)),
    );
  }

  private findGeneratedCi(property: any): Observable<CiReport | null> {
    const normalise = (value: unknown) => typeof value === 'string'
      ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '';
    const name = normalise(property.name);
    if (!name) return of(null);
    return this.dashboard$.pipe(
      switchMap(response => {
        const records: any[] = response?.data?.recentIntel ?? [];
        const matches = records.filter(record =>
          record.status === 'Completed' &&
          (record.propertyId ? (record.propertyId?._id ?? record.propertyId) === property._id : normalise(record.property) === name),
        );
        // A name-only match must be unique to avoid showing another property's CI.
        if (matches.length !== 1 || !matches[0].id) {
          // A miss in a cached dashboard must not become permanent for this
          // session if a generated report is linked shortly afterwards.
          this.dashboard$ = this.dashboardRequest();
          return of(null);
        }
        return this._PS.getCiRecord(matches[0].id).pipe(
          map(result => {
            const record = result?.data;
            const recordPropertyId = record?.propertyId?._id ?? record?.propertyId;
            if (recordPropertyId && recordPropertyId !== property._id) return null;
            return mapCiReport({ ...property, ciRecord: record });
          }),
        );
      }),
    );
  }

}
